import { useCallback, useEffect, useMemo, useState } from 'react';

import * as api from '@/features/backlog/api';
import { ActivityFeedItem } from '@/features/backlog/types';
import { stripMentions } from '@/features/backlog/mentions';
import { timeAgo } from '@/features/backlog/format';
import { taskPath, whiteboardPath } from '@/constants/routes';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import MemberAvatar from '@/components/member-avatar';
import { Bell, Cog, ExternalLink, MoreVertical, SignOut } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

/**
 * The desktop menu-bar / tray popover (loaded by the shell at `/tray` in a small frameless always-on-top
 * window, anchored to the tray N icon). Shows the same personal activity feed as the header bell, plus a
 * footer: Profile settings, Open app, and a ⋮ menu hosting Quit. Footer/rows talk to the Rust shell via
 * the injected `window.__sparkDesktop.tray(action, url)` beacon. Rendered only inside the desktop shell.
 */

type Bridge = { tray?: (action: string, url?: string) => void };
const tray = (action: string, url?: string): void =>
  (window as { __sparkDesktop?: Bridge }).__sparkDesktop?.tray?.(action, url);

const ts = (s: string): number => {
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
};

const FEED_DAYS = 7;

// The tray icon lives at the BOTTOM on Windows (system tray) and the TOP on macOS (menu bar), so the
// arrow points down-from-bottom on Windows and up-from-top on macOS, with the shadow cast away from it.
const isWin = typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);

export default function TrayPanel() {
  const { me } = useIdentity();
  const { members } = useAppData();
  const [list, setList] = useState<ActivityFeedItem[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const load = useCallback(() => {
    api.myActivityFeed(FEED_DAYS, 40).then(setList).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!me) return undefined;
    load();
    const t = window.setInterval(load, 60000);
    return () => window.clearInterval(t);
  }, [me, load]);

  // Refresh the moment the popover regains focus (the shell shows it on tray click).
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const openItem = (a: ActivityFeedItem) => {
    const path = a.whiteboard_id
      ? whiteboardPath(a.whiteboard_id) + (a.ref_id ? `?comment=${a.ref_id}` : '')
      : taskPath(a.board_slug, a.item_key);
    tray('open-item', window.location.origin + path);
  };

  const newest = list.length ? ts(list[0].created_at) : 0;

  return (
    <div
      className={cn(
        'relative flex h-screen w-screen justify-center overflow-hidden bg-transparent px-7',
        // Arrow side gets ~18px; the shadow side gets ~40px so the drop shadow isn't clipped.
        isWin ? 'pb-[18px] pt-10' : 'pb-10 pt-[18px]',
      )}
    >
      {/* Floating card: rounded corners + soft drop shadow. The transparent window + this padding give
          the shadow room so it isn't clipped, and overflow-hidden on the outer kills stray scrollbars. */}
      <div
        className={cn(
          'relative z-10 flex w-full max-w-[356px] flex-col overflow-hidden rounded-2xl border border-line bg-surface text-fg antialiased',
          // Cast the shadow AWAY from the tray icon (up on Windows, down on macOS).
          isWin ? 'shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.42)]' : 'shadow-[0_12px_32px_-8px_rgba(0,0,0,0.42)]',
        )}
      >
      {/* Header */}
      <header className="flex items-center gap-2 px-4 pb-2.5 pt-3.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-layer text-fg-muted">
          <Bell className="h-3.5 w-3.5" />
        </span>
        <span className="text-[13.5px] font-semibold tracking-tight text-fg-strong">Activity</span>
        <span className="ml-auto text-[11px] font-medium text-fg-ghost">last {FEED_DAYS} days</span>
      </header>

      {/* Feed */}
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2">
        {!me ? (
          <div className="px-3 py-16 text-center text-[12px] text-fg-faint">Loading…</div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-layer text-fg-ghost">
              <Bell className="h-5 w-5" />
            </span>
            <div className="text-[12.5px] font-medium text-fg-soft">You're all caught up</div>
            <div className="text-[11.5px] leading-relaxed text-fg-faint">
              Changes on items you own, watch, or are assigned show up here.
            </div>
          </div>
        ) : (
          list.map((a, i) => {
            const actor = a.actor_id ? memberById.get(a.actor_id) : undefined;
            const isNewest = i === 0 && newest > 0;
            return (
              <button
                key={`${a.item_id}-${a.created_at}-${i}`}
                type="button"
                onClick={() => openItem(a)}
                className="group flex w-full items-start gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-layer"
              >
                <MemberAvatar member={actor} size={28} className="mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] leading-snug text-fg-soft">
                    <span className="font-semibold text-fg">{actor?.name ?? a.actor_name ?? 'Someone'}</span> {a.summary}
                    {a.detail ? <span className="font-semibold text-fg"> {a.detail}</span> : null}
                  </div>
                  {a.snippet ? (
                    <div className="mt-0.5 truncate text-[11.5px] text-fg-faint">{stripMentions(a.snippet)}</div>
                  ) : null}
                  <div className="mt-1 flex items-center gap-x-1.5 text-[11px] text-fg-ghost">
                    <span className="shrink-0">{timeAgo(a.created_at)}</span>
                    {a.whiteboard_id ? (
                      <span className="min-w-0 flex-1 truncate text-fg-soft">🖊 {a.whiteboard_name || 'Whiteboard'}</span>
                    ) : (
                      <>
                        <span className="item-key shrink-0 whitespace-nowrap rounded px-1 py-px tnum text-[10px] font-semibold">
                          {a.item_key}
                        </span>
                        {a.item_title ? <span className="min-w-0 flex-1 truncate text-fg-soft">{a.item_title}</span> : null}
                      </>
                    )}
                    {a.relation === 'mention' ? (
                      <span className="shrink-0 whitespace-nowrap rounded bg-brand/15 px-1 py-px text-[10px] font-semibold text-brand">
                        mention
                      </span>
                    ) : null}
                  </div>
                </div>
                <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0 text-fg-ghost opacity-0 transition-opacity group-hover:opacity-100" />
                {isNewest ? <span className="sr-only">newest</span> : null}
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <footer className="relative flex items-center gap-1 border-t border-line bg-surface-raised/60 px-2.5 py-2">
        {me ? (
          <div className="mr-1 flex min-w-0 items-center gap-2 pl-1">
            <MemberAvatar member={me} size={24} className="shrink-0" />
            <span className="truncate text-[12px] font-medium text-fg-soft">{me.name}</span>
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => tray('open')}
            title="Open the app"
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[12.5px] font-medium text-fg-soft transition-colors hover:bg-layer"
          >
            <ExternalLink className="h-4 w-4 text-fg-muted" />
            Open app
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg',
              menuOpen && 'bg-layer-strong text-fg',
            )}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>
        {menuOpen ? (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute bottom-11 right-2 z-20 min-w-[184px] overflow-hidden rounded-xl border border-line bg-surface-raised p-1 shadow-2xl">
              <button
                type="button"
                onClick={() => tray('settings')}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12.5px] font-medium text-fg-soft transition-colors hover:bg-layer"
              >
                <Cog className="h-4 w-4 text-fg-muted" />
                Profile settings
              </button>
              <div className="my-1 h-px bg-line" />
              <button
                type="button"
                onClick={() => tray('quit')}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12.5px] font-medium text-fg-soft transition-colors hover:bg-rose-500/10 hover:text-rose-500"
              >
                <SignOut className="h-4 w-4" />
                Quit Spark Boards
              </button>
            </div>
          </>
        ) : null}
      </footer>
      </div>
      {/* Rounded arrow pointing at the tray icon. z-30 so it sits on the card's edge — its opaque fill
          hides the card's border at the centre so it merges. Points DOWN from the bottom on Windows
          (tray below), UP from the top on macOS (menu bar above). */}
      <span
        className={cn(
          'pointer-events-none absolute left-1/2 z-30 h-[15px] w-[15px] -translate-x-1/2 rotate-45 border-line bg-surface',
          isWin ? 'bottom-[11px] rounded-br-[4px] border-b border-r' : 'top-[11px] rounded-tl-[4px] border-l border-t',
        )}
      />
    </div>
  );
}
